import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, useRef, type ReactNode } from "react";

import appCss from "../styles.css?url";
// Critical CSS: the compiled stylesheet is ~20 KB gzip, small enough to inline
// directly into the document head in production. That removes the only
// render-blocking request on the critical path (HTML -> CSS -> paint), which is
// what was holding mobile FCP/LCP back. In dev we keep the <link> so Vite's CSS
// HMR keeps working.
import appCssInline from "../styles.css?inline";
import outfitLatinUrl from "../fonts/outfit-latin-357083.woff2?url";
import figtreeLatinUrl from "../fonts/figtree-latin-dab710.woff2?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { IntentPrefetcher } from "../components/perf/IntentPrefetcher";
import { ConversionTracker } from "../components/perf/ConversionTracker";
import { FloatingWhatsApp } from "../components/layout/FloatingWhatsApp";
import { organizationLd, localBusinessLd, websiteLd } from "../lib/seo";



function NotFoundComponent() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: import("@tanstack/react-router").ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

import { siteConfig } from "../config/site";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: `${siteConfig.name} — ${siteConfig.tagline}` },
      {
        name: "description",
        content: `${siteConfig.name} at ${siteConfig.contact.address.street}, ${siteConfig.contact.address.city}, ${siteConfig.contact.address.state}. Local SEO, technical SEO, GEO, and 24/7 growth support. Call ${siteConfig.contact.phone}.`,
      },

      { name: "author", content: siteConfig.name },
      { name: "theme-color", content: siteConfig.theme.colors.background },
      { name: "google-site-verification", content: "Uhhq4PdyF2k3bXBzTxHBeKJ5y51XIvmSxQaEmgHvjEA" },
      { name: "msvalidate.01", content: "FD561D3D651BFDFA570F79A338F7C67C" },
      { name: "yandex-verification", content: "a1b8020443d698c4" },
      { property: "og:site_name", content: siteConfig.name },
      { property: "og:type", content: "website" },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/fa0f4fda-e266-4bb9-862b-0ec45a3e3fd5" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/fa0f4fda-e266-4bb9-862b-0ec45a3e3fd5" },
    ],

    links: [
      ...(import.meta.env.PROD ? [] : [{ rel: "stylesheet", href: appCss }]),
      // Google SERP favicon: requires a square PNG that's a multiple of 48px.
      // Order matters — declare the highest-quality icon first so Google
      // (and modern browsers) prefer it over the legacy .ico fallback.
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg?v=9" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/icon-512.png?v=9" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/icon-192.png?v=9" },
      { rel: "icon", type: "image/png", sizes: "96x96", href: "/favicon-96x96.png?v=9" },
      { rel: "icon", type: "image/png", sizes: "48x48", href: "/favicon-48x48.png?v=9" },
      { rel: "icon", href: "/favicon.ico?v=9", sizes: "any" },
      { rel: "shortcut icon", href: "/favicon.ico?v=9" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png?v=9", sizes: "180x180" },
      { rel: "manifest", href: "/site.webmanifest?v=9" },

      // llms.txt — LLM-friendly site map (https://llmstxt.org)
      { rel: "alternate", type: "text/plain", href: "/llms.txt", title: "llms.txt" },
      { rel: "sitemap", type: "application/xml", href: "/sitemap.xml", title: "Sitemap" },
      // Fonts are self-hosted (src/fonts, bundled + fingerprinted by Vite), so
      // there is no third-party CSS -> gstatic request chain on the critical
      // path. Preload only the two latin subsets used above the fold.
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: outfitLatinUrl,
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: figtreeLatinUrl,
        crossOrigin: "anonymous",
      },


    ],
    scripts: [

      // Google Analytics 4 (gtag.js) — loaded lazily on first interaction or
      // after 4s idle so the tag never competes with LCP/INP on mobile.
      {
        children:
          "window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','G-8S1C8PTY1R',{send_page_view:true});(function(){var l=function(){if(window.__gaLoaded)return;window.__gaLoaded=1;var s=document.createElement('script');s.async=1;s.src='https://www.googletagmanager.com/gtag/js?id=G-8S1C8PTY1R';document.head.appendChild(s);};['click','keydown','touchstart','scroll','mousemove'].forEach(function(e){addEventListener(e,l,{once:true,passive:true});});setTimeout(l,4000);})();",
      },

      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            organizationLd(),
            websiteLd(),
          ],
        }),
      },

    ],

  }),
  shellComponent: RootShell,

  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});


function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        {import.meta.env.PROD ? (
          <style
            id="critical-css"
            // Full compiled stylesheet, inlined so the first paint never waits
            // on a second network round trip.
            dangerouslySetInnerHTML={{ __html: appCssInline }}
          />
        ) : null}
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  // Re-key the Outlet on pathname so each route change re-runs the fade-in.
  const pathname = useRouter().state.location.pathname;

  useEffect(() => {
    let cancelled = false;
    // Telemetry is never on the critical path: wait for an idle slice after
    // load so these chunks don't compete with LCP or hydration work.
    const start = () => {
      import("../lib/web-vitals-reporter").then((m) => {
        if (!cancelled) m.startWebVitalsReporting();
      });
      import("../lib/image-rum").then((m) => {
        if (!cancelled) m.startImageRum();
      });
    };
    const schedule = () => {
      const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number })
        .requestIdleCallback;
      if (ric) ric(start, { timeout: 4000 });
      else setTimeout(start, 2500);
    };
    if (document.readyState === "complete") schedule();
    else window.addEventListener("load", schedule, { once: true });
    return () => {
      cancelled = true;
    };
  }, []);

  // GA4 SPA pageviews: the initial view is sent by gtag('config'); every
  // client-side route change after that needs an explicit page_view.
  const firstPathRef = useRef(true);
  useEffect(() => {
    if (firstPathRef.current) {
      firstPathRef.current = false;
      return;
    }
    const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag;
    if (typeof gtag === "function") {
      gtag("event", "page_view", {
        page_path: pathname + window.location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }, [pathname]);



  return (
    <QueryClientProvider client={queryClient}>
      <IntentPrefetcher />
      <ConversionTracker />
      <FloatingWhatsApp 
        phoneNumber={siteConfig.contact.whatsapp.number}
        displayName={siteConfig.contact.whatsapp.display}
        initialMessage={siteConfig.contact.whatsapp.message}
      />

      <div key={pathname} className="animate-fade-in motion-reduce:animate-none">


        {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
        <Outlet />
      </div>
    </QueryClientProvider>
  );
}

